"use client"

import { useState, useEffect } from "react"
import { Search, Dog, ShoppingCart, X } from "lucide-react"
import Link from "next/link"
import { useRouter } from "next/navigation"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

// Define the structure of search results
type SearchResult = {
  id: string
  name: string
  image: string | null
}

export function Header() {
  const [showSearch, setShowSearch] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const [searchResults, setSearchResults] = useState<SearchResult[]>([])
  const [loading, setLoading] = useState(false)
  const router = useRouter()

  // Listen for toggle search event from Navigation component
  useEffect(() => {
    const handleToggleSearch = () => {
      setShowSearch((prev) => !prev)
    }

    // Add event listener
    document.addEventListener("toggleSearch", handleToggleSearch)

    // Clean up
    return () => {
      document.removeEventListener("toggleSearch", handleToggleSearch)
    }
  }, [])

  // Fetch search results from API
  const fetchSearchResults = async (query: string) => {
    if (!query) {
      setSearchResults([])
      return
    }

    setLoading(true)
    try {
      const response = await fetch(`/api/search?query=${query}`)
      if (!response.ok) throw new Error("Failed to fetch results")

      const data: SearchResult[] = await response.json()
      setSearchResults(data)
    } catch (error) {
      console.error("Search error:", error)
      setSearchResults([])
    } finally {
      setLoading(false)
    }
  }

  // Debounce search input
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      fetchSearchResults(searchQuery)
    }, 300)

    return () => clearTimeout(delayDebounce)
  }, [searchQuery])

  // Navigate to product page
  const handleSelectResult = (href: string) => {
    router.push(href)
    setSearchQuery("")
    setSearchResults([])
    setShowSearch(false)
  }

  return (
    <div className="sticky top-0 z-50 w-full bg-blue-500 shadow-md">
      <div className="container mx-auto px-4">
        <div className="flex flex-col md:flex-row items-center justify-between py-4">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-2 mb-4 md:mb-0">
            <Dog className="h-8 w-8 text-white" />
            <span className="text-white text-2xl font-bold">LV-Vet</span>
          </Link>

          {/* Search Bar - Desktop */}
          <div className="hidden md:block flex-1 w-full max-w-md mb-4 md:mb-0 px-4 relative">
            <div className="relative">
              <Input
                type="search"
                placeholder="Над 10,000 продукта"
                className="w-full bg-white pl-4 pr-10 py-2 rounded-md"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                aria-label="Search products"
              />
              <Search className="absolute right-3 top-2.5 h-5 w-5 text-gray-400" />
            </div>

            {/* Search Results Dropdown */}
            {searchQuery && (
              <div className="absolute w-full bg-white shadow-lg rounded-md mt-1 z-50 max-h-[60vh] overflow-y-auto">
                {loading ? (
                  <p className="p-3 text-gray-500">Търсене...</p>
                ) : searchResults.length > 0 ? (
                  searchResults.map((item) => (
                    <div
                      key={item.id}
                      className="p-3 border-b cursor-pointer hover:bg-gray-100 flex items-center gap-3"
                      onClick={() => handleSelectResult(`/product/${item.id}`)}
                    >
                      {item.image ? (
                        <img
                          src={item.image || "/placeholder.svg"}
                          alt={item.name}
                          className="w-10 h-10 object-cover rounded-md"
                        />
                      ) : (
                        <div className="w-10 h-10 bg-gray-300 rounded-md flex items-center justify-center">
                          <Dog className="h-5 w-5 text-gray-500" />
                        </div>
                      )}
                      <span>{item.name}</span>
                    </div>
                  ))
                ) : (
                  <p className="p-3 text-gray-500">Няма намерени резултати</p>
                )}
              </div>
            )}
          </div>

          {/* Contact & Actions */}
          <div className="flex items-center gap-4">
            <div className="flex flex-col items-center text-center">
              <span className="text-lg font-semibold text-white">0700 19 519</span>
              <span className="text-sm text-white">поръчайте по телефона</span>
            </div>
            <div className="flex items-center gap-4">
              {/* Search Icon - Mobile */}
              <Button
                variant="ghost"
                size="icon"
                className="md:hidden text-white hover:bg-blue-600"
                onClick={() => setShowSearch(!showSearch)}
                aria-label="Toggle search"
              >
                <Search className="h-6 w-6" />
              </Button>
              <Link href="/cart">
                <Button variant="ghost" size="icon" className="text-white hover:bg-blue-600" aria-label="Shopping cart">
                  <ShoppingCart className="h-6 w-6" />
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Mobile Search - Expandable */}
        <div
          className={cn(
            "md:hidden w-full pb-4 transition-all duration-300 ease-in-out overflow-hidden",
            showSearch ? "max-h-96 opacity-100" : "max-h-0 opacity-0",
          )}
        >
          <div className="relative">
            <Input
              type="search"
              placeholder="Над 10,000 продукта"
              className="w-full bg-white pl-4 pr-10 py-2 rounded-md"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoFocus={showSearch}
              aria-label="Search products"
            />
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 text-gray-400"
              onClick={() => setShowSearch(false)}
              aria-label="Close search"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Mobile Search Results */}
          {showSearch && searchQuery && (
            <div className="bg-white shadow-lg rounded-md mt-1 max-h-[50vh] overflow-y-auto">
              {loading ? (
                <p className="p-3 text-gray-500">Търсене...</p>
              ) : searchResults.length > 0 ? (
                searchResults.map((item) => (
                  <div
                    key={item.id}
                    className="p-3 border-b cursor-pointer hover:bg-gray-100 flex items-center gap-3"
                    onClick={() => handleSelectResult(`/product/${item.id}`)}
                  >
                    {item.image ? (
                      <img
                        src={item.image || "/placeholder.svg"}
                        alt={item.name}
                        className="w-10 h-10 object-cover rounded-md"
                      />
                    ) : (
                      <div className="w-10 h-10 bg-gray-300 rounded-md flex items-center justify-center">
                        <Dog className="h-5 w-5 text-gray-500" />
                      </div>
                    )}
                    <span>{item.name}</span>
                  </div>
                ))
              ) : (
                <p className="p-3 text-gray-500">Няма намерени резултати</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

